import { apiGetPaymentScheduleInfo } from '@/dashboard/api/policy';
import {
  DownloadPolicyDocumentResponse,
  InsuranceProviderResponse,
  PROVIDER_SOURCE,
  PolicyCoverCreateRequest,
  PolicyDetailsResponse,
  jove__app__policy_pause__to_model__PolicyPauseResponse as PolicyPauseResponse,
  PolicyState,
  QuoteResponse,
  apiCreatePolicyCover,
  apiCreatePolicyPause,
  apiGetInsuranceProviders,
  apiGetPoliciesWithDetails,
  apiGetPolicyPauses,
  apiGetQuoteDocuments,
  apiSendQuote,
  apiUpdatePolicy,
  apiUpdatePolicyPause,
  apiUpdateQuote,
  dateDiffInDays,
  jove__app__quote__to_model__QuoteUpdateRequest,
  transformDateStrToTimeString,
} from '@jove-frontend/uikit';
import { enableMapSet } from 'immer';
import { sortBy } from 'lodash';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface IPolicyState {
  policies: PolicyDetailsResponse[];
  policyMap: Map<string, PolicyDetailsResponse>;
  insuranceProviders: InsuranceProviderResponse[];
  providerQuote: QuoteResponse;
  quoteDocuments: DownloadPolicyDocumentResponse[];
  isLoading: boolean;
  paymentLoading: boolean;
  paymentParams?: { year: number | string };
  paymentScheduleInfo: any;
}

interface IPolicyActions {
  initialize: (config?: { queryUserId?: string }) => Promise<void>;
  fetchPolicies: (config?: { queryUserId?: string }) => Promise<void>;
  fetchPolicyPauses: (policyId: string) => Promise<void>;
  fetchInsuranceProviders: () => Promise<void>;
  updatePolicy: (args: Parameters<typeof apiUpdatePolicy>) => Promise<void>;
  cancelPolicy: (policyId: string) => Promise<void>;
  pausePolicy: (args: Parameters<typeof apiCreatePolicyPause>) => Promise<void>;
  updatePausePolicy: (args: Parameters<typeof apiUpdatePolicyPause>) => Promise<void>;
  addProduct: (args: PolicyCoverCreateRequest) => Promise<void>;
  calculatePauseAvailable: (policyId: string) => {
    totalPerYear: number;
    daysUsed: number;
    daysLeft: number;
    currentPause: PolicyPauseResponse;
    previousPauses: PolicyPauseResponse[];
  };
  updateQuote: (quoteId: string, payload: jove__app__quote__to_model__QuoteUpdateRequest) => Promise<boolean>;
  updatePaymentAllInfo: (args?: { year: number | string; queryUserId?: string }) => void;
}

interface IPolicyStore {
  state: IPolicyState;
  actions: IPolicyActions;
}

enableMapSet();

export const usePolicyStore = create<IPolicyStore>()(
  immer((set, get) => ({
    state: {
      policies: null,
      policyMap: new Map(),
      insuranceProviders: null,
      providerQuote: null,
      quoteDocuments: null,
      isLoading: false,
      paymentLoading: false,
      paymentScheduleInfo: null,
    },
    actions: {
      initialize: async (config) => {
        set((store) => {
          store.state.isLoading = true;
        });

        const { fetchPolicies, fetchInsuranceProviders } = get().actions;

        await fetchPolicies(config);
        await fetchInsuranceProviders();

        set((store) => {
          store.state.isLoading = false;
        });
      },
      fetchPolicies: async (config) => {
        set((store) => {
          store.state.isLoading = true;
        });

        const params: any = {};
        if (config?.queryUserId) {
          params.userId = config.queryUserId;
        }
        // Fetch policies only for CHUBB_UK provider
        const [res, err] = await apiGetPoliciesWithDetails(params);

        const statePriority = {
          [PolicyState.POLICY_PENDING]: 0,
          [PolicyState.POLICY_ACTIVE]: 1,
        };
        set((store) => {
          // Sort by state
          const list = sortBy(res?.data || [], (item) => {
            return statePriority[item.state] !== undefined ? statePriority[item.state] : 2;
          });
          store.state.policies = list;
          res?.data?.map((policy) => store.state.policyMap.set(policy.id, policy));
          store.state.isLoading = false;
        });
      },
      fetchPolicyPauses: async (policyId) => {
        const [pauses, err] = await apiGetPolicyPauses({ policyId });

        if (pauses) {
          set((store) => {
            store.state.policyMap.set(policyId, {
              ...store.state.policyMap.get(policyId),
            });
          });
        }
      },
      fetchInsuranceProviders: async () => {
        const [res, err] = await apiGetInsuranceProviders({});

        set((store) => {
          store.state.insuranceProviders = res?.data ?? [];
        });
      },
      updatePolicy: async (args) => {
        const [policy, err] = await apiUpdatePolicy(...args);

        if (policy) {
          set((store) => {
            const idx = store.state.policies.findIndex((policy) => policy.id === args[0]);
            store.state.policies[idx] = policy;
          });
        }
      },
      cancelPolicy: async (policyId) => {
        const [res, err] = await apiUpdatePolicy(policyId, {
          state: PolicyState.POLICY_CANCELLED,
          cancellationDate: new Date().toISOString(),
        });
      },
      pausePolicy: async (args) => {
        const [pause, err] = await apiCreatePolicyPause(...args);

        console.log(pause);
      },
      addProduct: async (args) => {
        const [res, err] = await apiCreatePolicyCover(args);
      },
      updatePausePolicy: async (args) => {
        const [pause, err] = await apiUpdatePolicyPause(...args);

        console.log(pause);
      },
      calculatePauseAvailable: (policyId) => {
        const { policyMap } = get().state;
        const policy = policyMap.get(policyId);

        const totalPerYear = 90;
        const daysUsed = policy.pauses.reduce<number>((acc, curr) => {
          const s = new Date(curr.pauseStartDate);
          const e = new Date(curr.pauseEndDate);

          return acc + dateDiffInDays(s, e);
        }, 0);
        const daysLeft = totalPerYear - daysUsed;

        const currentPause = policy.pauses.find(
          (pause) => pause.pauseStartDate === policy.pauseStartDate && pause.pauseEndDate === policy.pauseEndDate
        );
        const previousPauses = policy.pauses.filter((pause) => pause.id !== currentPause.id);

        return {
          totalPerYear,
          daysUsed,
          daysLeft,
          currentPause,
          previousPauses,
        };
      },
      updateQuote: async (quoteId, payload) => {
        const [quoteRes, err1] = await apiUpdateQuote(quoteId, payload);
        if (err1) return false;

        const [res, err2] = await apiSendQuote(quoteRes.id);
        if (err2) return false;

        const [documents, err3] = await apiGetQuoteDocuments(quoteId);
        if (err3) return false;

        set((store) => {
          store.state.providerQuote = res;
          store.state.quoteDocuments = documents.data;
        });
        return true;
      },
      // update payment schedule, monthly payment
      updatePaymentAllInfo: async (data) => {
        const mergeParams = { ...(get().state.paymentParams || {}), ...data };
        set((store) => {
          store.state.paymentLoading = true;
          store.state.paymentParams = mergeParams;
        });
        const params = {
          // provider_code: PROVIDER_SOURCE().CHUBB.value,
          ...mergeParams,
          user_id: data?.queryUserId,
        };
        delete params.queryUserId;

        const [res] = await apiGetPaymentScheduleInfo(params);

        if (res) {
          res.schedule = (res.schedule || []).map((item) => {
            item.premium = Number(item.premium);
            item.dateText = transformDateStrToTimeString(item.date, {
              format: 'MMM YYYY',
            });
            return item;
          });
          set((store) => {
            store.state.paymentLoading = false;
            store.state.paymentScheduleInfo = res;
          });
        }
        set((store) => {
          store.state.paymentLoading = false;
        });
      },
    },
  }))
);
